/**
 * Card "Pago automático Toku" — muestra al admin la CLABE virtual del cliente
 * en Toku y permite generar URL temp al portal Toku del cliente.
 *
 * rc47 — añadido al panel admin de detalle de cliente. La CLABE es estática
 * por cliente (Toku la asigna al crear el customer) y se cachea permanente
 * en `toku_entity_map.clabe`. Botón "Re-sincronizar" hace force-fetch como
 * escape hatch si algo se desincroniza.
 *
 * Estados:
 *  - Toku no configurado (kill-switch off) → card oculta.
 *  - Cliente sin sync Toku → mensaje + botón "Sincronizar con Toku".
 *  - Sync OK → CLABE en 4-4-4-3-3 con botón copiar + botón portal Toku.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Landmark,
  Copy,
  Check,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Loader2,
  CreditCard,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiCall } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { PagoCardDialog } from "./PagoCardDialog";

type ClabeResp = {
  success: boolean;
  clabe: string | null;
  cached: boolean;
  toku_customer_id: string | null;
  fetched_at: string | null;
  error: string | null;
  skipped: boolean | null;
  needs_curp: boolean | null;
};

type PortalSessionResp = {
  success: boolean;
  portal_url: string | null;
  expires_at: number | null;
  expires_in_minutes: number;
  error: string | null;
  skipped: boolean | null;
};

/** Formatea CLABE 18 dígitos como "646 010 32850 8908 057" para legibilidad. */
function formatClabe(clabe: string): string {
  if (!clabe || clabe.length !== 18) return clabe;
  return `${clabe.slice(0, 3)} ${clabe.slice(3, 6)} ${clabe.slice(6, 11)} ${clabe.slice(11, 15)} ${clabe.slice(15, 18)}`;
}

export function TokuClabeCard({
  clienteId, clienteNombre, clienteCodigo, empresaId,
}: {
  clienteId: string;
  // rc123 — para la tarjeta de pago descargable (beneficiario segun empresa)
  clienteNombre?: string | null;
  clienteCodigo?: string | null;
  empresaId?: number;
}) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [pagoOpen, setPagoOpen] = useState(false);

  const { data, isLoading } = useQuery<ClabeResp>({
    queryKey: ["cliente", clienteId, "toku-clabe"],
    queryFn: () =>
      apiCall<ClabeResp>(
        "get",
        `/api/v1/integrations/payments/clientes/${clienteId}/clabe`,
      ),
    // Cache 5 min en front — backend tiene cache permanente, no necesitamos
    // refetch agresivo. Si admin presiona "Re-sincronizar" invalidamos.
    staleTime: 5 * 60 * 1000,
  });

  const resyncMut = useMutation({
    mutationFn: () =>
      apiCall<ClabeResp>(
        "get",
        `/api/v1/integrations/payments/clientes/${clienteId}/clabe?force=true`,
      ),
    onSuccess: (resp) => {
      qc.setQueryData(["cliente", clienteId, "toku-clabe"], resp);
      if (resp.success) {
        toast.success("CLABE re-sincronizada con Toku");
      } else {
        toast.error(resp.error || "No se pudo re-sincronizar");
      }
    },
    onError: (e: any) => {
      toast.error(e?.message || "Error al re-sincronizar");
    },
  });

  const portalMut = useMutation({
    mutationFn: () =>
      apiCall<PortalSessionResp>(
        "post",
        `/api/v1/integrations/payments/clientes/${clienteId}/portal-session`,
      ),
    onSuccess: (resp) => {
      if (resp.success && resp.portal_url) {
        window.open(resp.portal_url, "_blank", "noopener,noreferrer");
        toast.success("Portal Toku abierto en nueva pestaña (expira en 5 min)");
      } else {
        toast.error(resp.error || "No se pudo generar URL del portal");
      }
    },
    onError: (e: any) => {
      toast.error(e?.message || "Error al generar URL del portal");
    },
  });

  // Si Toku no está configurado, ocultamos la tarjeta — no añadimos ruido
  // al admin si la integración está apagada.
  if (data?.skipped) return null;

  const copy = () => {
    if (!data?.clabe) return;
    navigator.clipboard.writeText(data.clabe);
    setCopied(true);
    toast.success("CLABE copiada");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Landmark size={14} />
          Pago automático Toku
          {data?.success && data.clabe && (
            <Badge
              variant="outline"
              className="text-emerald-700 border-emerald-200 bg-emerald-50"
            >
              CLABE activa
            </Badge>
          )}
          {data && !data.success && !isLoading && (
            <Badge
              variant="outline"
              className="text-amber-700 border-amber-200 bg-amber-50"
            >
              No sincronizado
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 size={12} className="animate-spin" />
            Cargando CLABE…
          </div>
        ) : data?.success && data.clabe ? (
          <>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              CLABE virtual asignada por Toku. El cliente puede transferir a
              esta cuenta desde cualquier banco — el pago se acredita
              automáticamente a su factura.
            </p>

            <div>
              <div className="text-2xs uppercase tracking-wide text-muted-foreground mb-1.5 font-medium">
                CLABE interbancaria
              </div>
              <div className="flex gap-1.5">
                <code className="flex-1 rounded-md bg-muted/60 border border-border px-3 py-2 text-sm font-mono select-all tracking-wide">
                  {formatClabe(data.clabe)}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copy}
                  title="Copiar CLABE"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              </div>
            </div>

            <dl className="mt-3 grid grid-cols-[140px_1fr] gap-y-1.5 text-xs">
              {data.toku_customer_id && (
                <>
                  <dt className="text-muted-foreground">Customer Toku</dt>
                  <dd className="font-mono text-xs truncate" title={data.toku_customer_id}>
                    {data.toku_customer_id}
                  </dd>
                </>
              )}
              {data.fetched_at && (
                <>
                  <dt className="text-muted-foreground">Sincronizada</dt>
                  <dd>
                    {formatDate(data.fetched_at)}
                    {data.cached && (
                      <span className="ml-1 text-2xs text-muted-foreground">
                        (cache)
                      </span>
                    )}
                  </dd>
                </>
              )}
            </dl>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => portalMut.mutate()}
                disabled={portalMut.isPending}
              >
                {portalMut.isPending ? (
                  <Loader2 size={12} className="mr-1.5 animate-spin" />
                ) : (
                  <ExternalLink size={12} className="mr-1.5" />
                )}
                Abrir portal Toku
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => resyncMut.mutate()}
                disabled={resyncMut.isPending}
                title="Forzar fetch desde Toku (la CLABE es estática, esto raramente se necesita)"
              >
                {resyncMut.isPending ? (
                  <Loader2 size={12} className="mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw size={12} className="mr-1.5" />
                )}
                Re-sincronizar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPagoOpen(true)}
                title="Imagen con CLABE + beneficiario, lista para mandar al cliente (no toca Toku)"
              >
                <CreditCard size={12} className="mr-1.5" />
                Tarjeta de pago
              </Button>
            </div>
            <PagoCardDialog
              open={pagoOpen}
              onOpenChange={setPagoOpen}
              clabe={data.clabe}
              empresaId={empresaId}
              clienteNombre={clienteNombre}
              clienteCodigo={clienteCodigo}
            />
          </>
        ) : (
          // No-success / no-clabe: mostrar mensaje + botón sincronizar
          <div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed mb-3">
              <AlertTriangle size={12} className="mt-0.5 text-amber-600 flex-shrink-0" />
              <div>
                {data?.needs_curp ? (
                  <>
                    Este cliente no tiene CURP/RFC registrado y la cuenta Toku
                    en sandbox requiere uno para sincronizar. Edita el cliente
                    y agrega su CURP o RFC.
                  </>
                ) : data?.error ? (
                  <>{data.error}</>
                ) : (
                  <>
                    Este cliente aún no está sincronizado con Toku — se
                    sincroniza automáticamente al pedir su CLABE.
                  </>
                )}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => resyncMut.mutate()}
              disabled={resyncMut.isPending}
            >
              {resyncMut.isPending ? (
                <Loader2 size={12} className="mr-1.5 animate-spin" />
              ) : (
                <RefreshCw size={12} className="mr-1.5" />
              )}
              Sincronizar con Toku
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
